// Lo puro de Administración · Checklists (handoff autogestión 2026-10-29
// §5, plan 2026-10-30 Fase 3, T-ljhq — sólo lectura + asignar en esta
// ronda): las palabras de la vertical, el meta de cada plantilla
// («{n} ítems · {frecuencia} · {alcance}»), el orden de la lista
// (inactivas al final), la pill de severidad sugerida y el plan de
// guardado del drawer Asignar. Sin React ni fechas: todo lo que se pueda
// probar sin montar nada vive aquí.

import type { ChecklistTemplateAdminSummary, RecipeMeResponse } from "@bitacorapro/api-types";
import { positiveSeverityKeys } from "@bitacorapro/core-engine";
import { frequencyLabel as cadenceFrequencyLabel } from "../mantenciones/cadence";

type WordsRecipe = Pick<RecipeMeResponse, "labels">;

export type ChecklistWords = {
  /** «Tienda». */
  asset: string;
  /** «tienda». */
  assetLower: string;
  /** «Tiendas». */
  assetPlural: string;
  /** «tiendas». */
  assetPluralLower: string;
};

function lower(s: string): string {
  return s.charAt(0).toLowerCase() + s.slice(1);
}

// Los sustantivos salen de la receta (recipe.labels), nunca de un texto
// fijo; los fallbacks son los de adminWords para el instante antes de
// que cargue.
export function checklistWords(recipe: WordsRecipe | undefined): ChecklistWords {
  const asset = recipe?.labels?.asset ?? "Activo";
  const assetPlural = recipe?.labels?.assetPlural ?? "Activos";
  return { asset, assetLower: lower(asset), assetPlural, assetPluralLower: lower(assetPlural) };
}

export function pluralize(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

// La frecuencia: la misma convención que Mantenciones (7 = Semanal,
// 14 = Quincenal, 30 = Mensual, 90 = Trimestral, si no «Cada n …»).
// cadenceDays es nulo cuando la plantilla no calcula «al día»
// (ADR-0068): se dice, no se oculta.
export function frequencyLabel(cadenceDays: number | null): string {
  if (cadenceDays === null) return "Sin frecuencia";
  return cadenceFrequencyLabel(cadenceDays);
}

// El alcance: una plantilla de marca aplica a toda la marca (o a toda la
// organización cuando no tiene marca; el handoff dice «Toda la marca» en
// ambos casos); una de tiendas, a las que la llevan hoy.
export function scopeLabel(
  template: Pick<ChecklistTemplateAdminSummary, "scope" | "assignedAssetCount">,
  words: ChecklistWords
): string {
  if (template.scope === "brand") return "Toda la marca";
  return pluralize(template.assignedAssetCount, words.assetLower, words.assetPluralLower);
}

// «14 ítems · Mensual · Toda la marca». itemCount es opcional en el Zod
// (fixtures viejas); el servidor siempre lo manda y sin él se lee 0.
export function templateMeta(
  template: Pick<ChecklistTemplateAdminSummary, "scope" | "assignedAssetCount" | "cadenceDays"> & {
    itemCount?: number | undefined;
  },
  words: ChecklistWords
): string {
  const items = pluralize(template.itemCount ?? 0, "ítem", "ítems");
  return `${items} · ${frequencyLabel(template.cadenceDays)} · ${scopeLabel(template, words)}`;
}

// La lista: activas primero en el orden del servidor (displayOrder),
// inactivas al final en el suyo. Estable: dos plantillas con el mismo
// estado no se reordenan entre sí.
export function sortTemplates<T extends Pick<ChecklistTemplateAdminSummary, "active">>(
  templates: readonly T[]
): T[] {
  return [...templates.filter((t) => t.active), ...templates.filter((t) => !t.active)];
}

// Qué plantilla queda seleccionada: la pedida en la URL si existe, si no
// la primera de la lista ordenada; null sin plantillas.
export function resolveSelectedId<T extends Pick<ChecklistTemplateAdminSummary, "id" | "active">>(
  templates: readonly T[],
  requested: string | null
): string | null {
  if (requested && templates.some((t) => t.id === requested)) return requested;
  return sortTemplates(templates)[0]?.id ?? null;
}

// --- Severidad sugerida -------------------------------------------------

export type SeverityTone = "crit" | "warn" | "low" | "ok";

export type SeverityPill = {
  /** «Sev. crítica»: la etiqueta de la receta en minúscula tras «Sev.». */
  label: string;
  /** Clase st-pill--{tone}; null cuando la clave no está en la receta
   *  (se pinta neutra, con la clave tal cual, nunca un color inventado). */
  tone: SeverityTone | null;
};

type RecipeSeverity = RecipeMeResponse["severities"][number];

// El tono sale de la receta, no de la clave (sin ramas por slug). Qué
// nivel es «conforme» lo dice `conformance: "positive"` (ADR-0137,
// positiveSeverityKeys): el rank sólo ordena y no distingue un conforme
// de un defecto leve (en postventa el rank 0 «Estética» es un defecto).
// Un nivel positivo es --ok; entre los negativos la más alta es --crit,
// la más baja --low y las del medio --warn. Con dos niveles negativos
// quedan --low y --crit; con uno, --crit.
export function severityPill(
  key: string | null | undefined,
  severities: readonly RecipeSeverity[]
): SeverityPill | null {
  if (!key) return null;
  const sev = severities.find((s) => s.key === key);
  if (!sev) return { label: `Sev. ${key}`, tone: null };
  const label = `Sev. ${lower(sev.label)}`;
  const positive = positiveSeverityKeys({ severities });
  if (positive.has(sev.key)) return { label, tone: "ok" };
  const negativeRanks = severities.filter((s) => !positive.has(s.key)).map((s) => s.rank);
  const max = Math.max(...negativeRanks);
  const min = Math.min(...negativeRanks);
  if (sev.rank >= max) return { label, tone: "crit" };
  if (sev.rank <= min) return { label, tone: "low" };
  return { label, tone: "warn" };
}

// --- Drawer Asignar -------------------------------------------------------

export type TemplateScope = ChecklistTemplateAdminSummary["scope"];

export function sameIdSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const setB = new Set(b);
  return a.every((id) => setB.has(id));
}

export type AssignmentPlan =
  | { kind: "none" }
  | { kind: "put"; assetIds: string[] }
  | { kind: "patch"; body: { scope: "assets"; assetIds: string[] } | { scope: "brand" } };

// Qué request guarda la asignación, la misma lógica que Mantenciones:
//   - mismo alcance «tiendas», cambia la lista → PUT /:id/assets
//   - marca → tiendas → PATCH { scope: "assets", assetIds } (sin tiendas
//     el backend responde CHECKLIST_TEMPLATE_SCOPE_EMPTY; aquí no se
//     habilita Guardar)
//   - tiendas → marca → PATCH { scope: "brand" }
//   - nada cambió → none (Guardar deshabilitado)
export function assignmentPlan(args: {
  originalScope: TemplateScope;
  scope: TemplateScope;
  originalIds: readonly string[];
  selectedIds: readonly string[];
}): AssignmentPlan {
  const { originalScope, scope, originalIds, selectedIds } = args;
  if (scope !== originalScope) {
    if (scope === "assets") {
      if (selectedIds.length === 0) return { kind: "none" };
      return { kind: "patch", body: { scope: "assets", assetIds: [...selectedIds] } };
    }
    return { kind: "patch", body: { scope: "brand" } };
  }
  if (scope === "assets" && !sameIdSet(selectedIds, originalIds)) {
    if (selectedIds.length === 0) return { kind: "none" };
    return { kind: "put", assetIds: [...selectedIds] };
  }
  return { kind: "none" };
}

// Códigos que GET/PUT /checklist-templates/:id/assets y PATCH devuelven
// hoy → la frase que ve el gerente. El mensaje crudo nunca se muestra.
export function assignErrorMessage(code: string | null, words: ChecklistWords): string {
  switch (code) {
    case "CHECKLIST_TEMPLATE_BRAND_MISMATCH":
      return `Una o más ${words.assetPluralLower} no pertenecen a la marca de este checklist. Revisa la selección.`;
    case "CHECKLIST_TEMPLATE_SCOPE_EMPTY":
      return `Elige al menos una ${words.assetLower} para «${words.assetPlural} específicas».`;
    case "CHECKLIST_CONFIG_FORBIDDEN":
    case "ROLE_READ_ONLY":
      return "Tu rol no puede cambiar la asignación.";
    default:
      return "No pudimos guardar la asignación. Intenta de nuevo.";
  }
}
